import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';

import { Card, CalloutOutline, Flex, Box, Button, Collapse, Text, Icon, Select as BaseSelect } from 'core/components';
import { formConsumer, Field, InputGroup, InputMultiple, Switch, Select } from 'core/form';
import { FaCheck } from 'react-icons/fa';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import { TCP_FLAG_OPTIONS, OPERATOR_OPTIONS, PROTOCOL_OPTIONS } from '@kentik/ui-shared/mitigations/constants';

export const FlowSpecMatchHeader = ({ group, ...props }) => (
  <Flex align="center" mb={1} {...props}>
    <Icon icon={FaCheck} color="success" style={{ fontSize: 12, marginRight: 4 }} />
    <Text small muted>
      <Text fontWeight="bold" color="success">
        Include
      </Text>{' '}
      results matching{' '}
      <Text fontWeight="bold" color="primary">
        {group ? 'All' : 'Any'}
      </Text>{' '}
      of the following conditions:
    </Text>
  </Flex>
);

const handleRightSideOptionChange = (value, field) => {
  field.setValue(value);
};

const FlowSpecMatch = ({
  field,
  idx,
  matchField,
  rightSideOptions,
  valueOptions,
  onQuery,
  disabled,
  mb,
  operators
}) => (
  <Box>
    <Flex align="flex-start" pr={1} mb={mb}>
      <Box mr={1}>
        <Field
          small
          showLabel={false}
          field={matchField.operator}
          options={operators || OPERATOR_OPTIONS}
          inputStyle={{ minWidth: 175 }}
          placeholder="Choose an operator..."
          disabled={disabled}
          mb={0}
        >
          <Select menuWidth={175} />
        </Field>
      </Box>
      <Box mr={1}>
        {((valueOptions && !rightSideOptions) || onQuery) && (
          <Field
            showLabel={false}
            field={matchField.value}
            placeholder="Select value..."
            options={valueOptions}
            validateOptions
            disabled={disabled}
            inputStyle={{ minWidth: 260 }}
            mb={0}
          >
            <Select autoComplete exactMatch showFilter onQuery={onQuery} menuWidth={260} />
          </Field>
        )}
        {(!valueOptions || (valueOptions && rightSideOptions)) && !onQuery && (
          <Field
            showLabel={false}
            field={matchField.value}
            disabled={disabled}
            inputStyle={{ minWidth: 260 }}
            options={null} /* need this to handle toggling from options to no options */
            mb={0}
          >
            <InputGroup
              rightElement={
                rightSideOptions && valueOptions ? (
                  <BaseSelect
                    autoComplete
                    exactMatch
                    showFilter
                    options={valueOptions}
                    showLabel={false}
                    placeholder="Preset values.."
                    onChange={(value) => handleRightSideOptionChange(value, matchField.value)}
                    values={matchField.value.getValue()}
                    menuWidth="auto"
                    small
                  />
                ) : null
              }
            />
          </Field>
        )}
      </Box>
      {!disabled && (
        <Button
          minimal
          intent="danger"
          onClick={() => {
            field.remove(idx);

            if (field.size() === 0 && field.parent) {
              field.parent.remove(field.parent.fieldStates.findIndex((groupField) => groupField.range === field));
            }
          }}
          icon="cross"
        />
      )}
    </Flex>
  </Box>
);

export const matchFieldTypeHash = {
  icmpTypes: 'icmp_type',
  icmpCodes: 'icmp_code',
  tcpFlags: 'tcp_flags',
  packetLengths: 'packet_length',
  dscps: 'dscp',
  fragments: 'fragment'
};

@inject('$dictionary', '$lookups')
@formConsumer
@observer
export default class FlowSpecMatchField extends Component {
  state = { isOpen: false };

  componentDidMount() {
    const { form, matchFieldType } = this.props;

    this.setState({ isOpen: form.getValue(`flowspec.${matchFieldType}.enabled`) });
  }

  addGroup = () => {
    const { form, matchFieldType } = this.props;
    const groupField = form.getField(`flowspec.${matchFieldType}.value.groups`);
    const groupFieldState = groupField.add();
    groupFieldState.range.add();
  };

  onHeaderSwitchClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  onEnabledChange = (field, value) => {
    const { form, matchFieldType } = this.props;
    form.setValue(`flowspec.${matchFieldType}.infer`, false);
    this.setState(() => ({ isOpen: value }));
  };

  getValueOptions = (matchFieldType) => {
    const { $dictionary } = this.props;

    switch (matchFieldType) {
      case 'tcpFlags':
        return TCP_FLAG_OPTIONS;
      case 'protocols':
        return PROTOCOL_OPTIONS;
      default:
        return $dictionary.filterFieldValueOptions[matchFieldTypeHash[matchFieldType]] || null;
    }
  };

  render() {
    const { form, matchFieldType, showInferField = false, operators, $lookups, onInferChange } = this.props;

    const { isOpen } = this.state;
    const matchesField = form.getField(`flowspec.${matchFieldType}.value.predicates`);
    const valueOptions = this.getValueOptions(matchFieldType);
    const onQuery = $lookups.getAutoCompleteFilterHandler(matchFieldType);
    const matchFieldDisabled = !form.getValue(`flowspec.${matchFieldType}.enabled`);
    const inferField = showInferField && form.getField(`flowspec.${matchFieldType}.infer`);
    const matchFieldMatchDisabled = showInferField && form.getValue(`flowspec.${matchFieldType}.infer`);
    const matchFieldIsPort = matchFieldType === 'srcPorts' || matchFieldType === 'dstPorts';
    const groupField = form.getField(`flowspec.${matchFieldType}.value.groups`);

    return (
      <Box borderTop="thin" py="12px">
        <Flex flexColumn>
          <Flex justifyContent="space-between" flex={1}>
            <Field
              name={`flowspec.${matchFieldType}.enabled`}
              showLabel={false}
              onClick={this.onHeaderSwitchClick}
              onChange={this.onEnabledChange}
              helpTextStyle={{ marginBottom: -5 }}
              mb={0}
            >
              <Switch showFieldLabel />
            </Field>

            {showInferField && (
              <Field
                name={`flowspec.${matchFieldType}.infer`}
                showLabel={false}
                showError={false}
                disabled={matchFieldDisabled}
                onClick={this.onHeaderSwitchClick}
                onChange={onInferChange}
                mb={0}
              >
                <Switch showFieldLabel />
              </Field>
            )}
          </Flex>
        </Flex>
        {inferField && inferField.errors && (
          <Flex mt={1}>
            <Text color="danger" small>
              {/* there is only one error ever, and it's about policies using the method that get invalidated if dimension gets inferred */}
              {inferField.errors[0]}
            </Text>
          </Flex>
        )}
        <Collapse isOpen={isOpen}>
          {!matchFieldMatchDisabled && (
            <Box mt={1}>
              <FlowSpecMatchHeader />
            </Box>
          )}
          {matchFieldMatchDisabled && !matchFieldIsPort && (
            <CalloutOutline mt="12px" intent="warning">
              <Flex>
                <Icon icon={BsFillExclamationTriangleFill} color="warning" mr={1} />
                <Text as="div">To use this method for a manual mitigation, turn off Infer from Alarm.</Text>
              </Flex>
            </CalloutOutline>
          )}

          {!matchFieldMatchDisabled && (
            <>
              <InputMultiple field={matchesField} addButtonText="Add">
                {({ field }) => (
                  <Box>
                    {field.map((matchField, idx) => (
                      <FlowSpecMatch
                        key={field._id + matchField.operator._id + matchField.value._id}
                        field={field}
                        matchField={matchField}
                        label={matchFieldType}
                        idx={idx}
                        valueOptions={valueOptions}
                        rightSideOptions={matchFieldType === 'protocols'}
                        onQuery={onQuery}
                        disabled={matchFieldDisabled || matchFieldMatchDisabled}
                        mb={1}
                        operators={operators}
                      />
                    ))}
                  </Box>
                )}
              </InputMultiple>

              <InputMultiple field={groupField}>
                {({ field }) => (
                  <>
                    {field.map((group) => (
                      <Card key={field._id + group.predicates._id} my="12px" p={1}>
                        <FlowSpecMatchHeader group />
                        <Box>
                          {group.predicates.map((predicateField, idx) => (
                            <FlowSpecMatch
                              key={field._id + predicateField.operator._id + predicateField.value._id}
                              field={group.predicates}
                              matchField={predicateField}
                              label={matchFieldType}
                              idx={idx}
                              valueOptions={valueOptions}
                              onQuery={onQuery}
                              disabled={matchFieldDisabled || matchFieldMatchDisabled}
                              mb={idx === group.predicates.size() - 1 ? 0 : 1}
                              operators={operators}
                            />
                          ))}

                          <Box pt={1}>
                            <Button
                              minimal
                              icon="plus"
                              text="Add Condition"
                              className="translucent"
                              onClick={() => group.predicates.add()}
                              disabled={matchFieldDisabled || matchFieldMatchDisabled}
                            />
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </>
                )}
              </InputMultiple>

              <Flex pt={0} mt="12px">
                <Button
                  mr={1}
                  icon="plus"
                  text="Add Condition"
                  className="translucent"
                  minimal
                  onClick={() => matchesField.add()}
                  disabled={matchFieldDisabled || matchFieldMatchDisabled}
                />
                <Button
                  style={{ marginLeft: 8 }}
                  icon="plus"
                  text="Add Group"
                  className="translucent"
                  minimal
                  onClick={this.addGroup}
                  disabled={matchFieldDisabled || matchFieldMatchDisabled}
                />
              </Flex>
            </>
          )}
        </Collapse>
      </Box>
    );
  }
}
